import { useCallback, useEffect, useState } from 'react';
import {
  supplyPlanningApi,
  type CapacityModel,
  type SupplyAllocation,
  type SupplyConstraint,
  type SupplyDashboard,
  type SupplyPlan,
  type SupplyRecommendation,
  type SupplyRisk,
  type SupplyScenario,
  type SupplySource,
} from '../../api/supplyplanning';

type Tab = 'dashboard' | 'network' | 'capacity' | 'allocation' | 'constraints' | 'risks' | 'recommendations' | 'scenarios';
type Theme = 'light' | 'dark';

function fmtMoney(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function SupplyPlanningPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<SupplyDashboard | null>(null);
  const [plan, setPlan] = useState<SupplyPlan | null>(null);
  const [sources, setSources] = useState<SupplySource[]>([]);
  const [capacities, setCapacities] = useState<CapacityModel[]>([]);
  const [allocations, setAllocations] = useState<SupplyAllocation[]>([]);
  const [constraints, setConstraints] = useState<SupplyConstraint[]>([]);
  const [risks, setRisks] = useState<SupplyRisk[]>([]);
  const [recommendations, setRecommendations] = useState<SupplyRecommendation[]>([]);
  const [scenarios, setScenarios] = useState<SupplyScenario[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, plans] = await Promise.all([supplyPlanningApi.dashboard(), supplyPlanningApi.listPlans()]);
      setDashboard(dash);
      const p = plans[0] ?? null;
      setPlan(p);
      if (!p) return;
      const [src, caps, alloc, cons, rsk, recs, sc] = await Promise.all([
        supplyPlanningApi.listSources(p.id),
        supplyPlanningApi.listCapacities(p.id),
        supplyPlanningApi.listAllocations(p.id),
        supplyPlanningApi.listConstraints(p.id),
        supplyPlanningApi.listRisks(p.id),
        supplyPlanningApi.listRecommendations(p.id),
        supplyPlanningApi.listScenarios(p.id),
      ]);
      setSources(src);
      setCapacities(caps);
      setAllocations(alloc);
      setConstraints(cons);
      setRisks(rsk);
      setRecommendations(recs);
      setScenarios(sc);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load supply planning');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const shell = dark ? 'min-h-screen bg-slate-950 text-slate-100' : 'min-h-screen bg-slate-50 text-slate-900';
  const card = dark ? 'rounded-xl border border-slate-800 bg-slate-900 p-5' : 'rounded-xl border border-slate-200 bg-white p-5 shadow-sm';
  const header = dark ? 'border-b border-slate-800 bg-slate-900' : 'border-b border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-600';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'network', label: 'Supply Network' },
    { id: 'capacity', label: 'Capacity' },
    { id: 'allocation', label: 'Allocation' },
    { id: 'constraints', label: 'Constraints' },
    { id: 'risks', label: 'Risk Dashboard' },
    { id: 'recommendations', label: 'Recommendations' },
    { id: 'scenarios', label: 'Scenarios' },
  ];

  return (
    <div className={shell}>
      <div className={`${header} px-6 py-4`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Enterprise Supply Planning</h1>
            <p className={`mt-1 text-sm ${muted}`}>Supply network planning, capacity optimization, constraint-based allocation, and AI supply orchestration</p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg px-3 py-2 text-sm ${dark ? 'bg-slate-800' : 'bg-slate-100'}`} aria-label="Toggle dark mode">{dark ? 'Light' : 'Dark'}</button>
            <button type="button" onClick={refresh} className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700">Refresh</button>
          </div>
        </div>
        {plan && (
          <p className={`mt-2 text-sm ${muted}`}>
            {plan.name} · Fulfillment {plan.fulfillment_rate.toFixed(0)}% · Health {plan.health_score.toFixed(0)} · Resilience {plan.resilience_index.toFixed(0)}
          </p>
        )}
      </div>
      {error && <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300" role="alert">{error}</div>}
      <div className={`${header} px-6`}>
        <nav className="flex gap-1 overflow-x-auto" role="tablist">
          {tabs.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium ${tab === t.id ? 'border-teal-600 text-teal-600' : 'border-transparent text-slate-500'}`}>{t.label}</button>
          ))}
        </nav>
      </div>
      <div className="p-6">
        {tab === 'dashboard' && dashboard && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Active Plans', value: dashboard.active_plans },
              { label: 'Avg Fulfillment', value: `${dashboard.avg_fulfillment_rate.toFixed(0)}%` },
              { label: 'Service Level', value: `${dashboard.avg_service_level.toFixed(0)}%` },
              { label: 'Supply Reliability', value: `${dashboard.supply_reliability.toFixed(0)}%` },
              { label: 'Capacity Utilization', value: `${dashboard.capacity_utilization.toFixed(0)}%` },
              { label: 'Active Constraints', value: dashboard.constraint_count },
              { label: 'Open Risks', value: dashboard.open_risks },
              { label: 'Health Score', value: dashboard.health_score.toFixed(0) },
              { label: 'Resilience Index', value: dashboard.resilience_index.toFixed(0) },
              { label: 'Supply Cost', value: fmtMoney(dashboard.supply_cost) },
              { label: 'Allocation Efficiency', value: `${dashboard.allocation_efficiency.toFixed(0)}%` },
              { label: 'Pending Recommendations', value: dashboard.pending_recommendations },
            ].map((m) => (
              <div key={m.label} className={card}><p className={`text-sm ${muted}`}>{m.label}</p><p className="mt-1 text-2xl font-semibold">{m.value}</p></div>
            ))}
          </div>
        )}
        {tab === 'network' && sources.map((s) => (
          <div key={s.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{s.source_name}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{s.source_type.replace(/_/g, ' ')} · Available {s.available_qty.toLocaleString()} · Lead time {s.lead_time_days}d · Reliability {(s.reliability * 100).toFixed(0)}%</p>
          </div>
        ))}
        {tab === 'capacity' && capacities.map((c) => (
          <div key={c.id} className={`${card} mb-4`}>
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{c.node_name}</h3>
              {c.constraint_active && <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-900 dark:text-amber-200">Constraint</span>}
            </div>
            <p className={`mt-1 text-sm ${muted}`}>{c.node_type} · Utilization {c.utilization_pct.toFixed(0)}% · Available {c.available_capacity.toLocaleString()}</p>
            <div className="mt-2 h-2 rounded-full bg-slate-200 dark:bg-slate-700">
              <div className="h-2 rounded-full bg-teal-600" style={{ width: `${Math.min(c.utilization_pct, 100)}%` }} />
            </div>
          </div>
        ))}
        {tab === 'allocation' && allocations.map((a) => (
          <div key={a.id} className={`${card} mb-4`}>
            <h3 className="font-medium">Fulfillment {a.fulfillment_pct.toFixed(0)}% · {a.primary_source_name}</h3>
            <p className={`mt-1 text-sm ${muted}`}>Demand {a.demand_qty.toLocaleString()} · Allocated {a.allocated_qty.toLocaleString()} · Unmet {a.unmet_qty.toLocaleString()}</p>
            <p className="mt-2 text-sm">{a.allocation_reason}</p>
          </div>
        ))}
        {tab === 'constraints' && constraints.map((c) => (
          <div key={c.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{c.type.replace(/_/g, ' ')} · {c.severity}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{c.description}</p>
            <p className="mt-2 text-sm">Impact: {c.impact}</p>
            <p className="mt-1 text-sm">Mitigation: {c.mitigation}</p>
          </div>
        ))}
        {tab === 'risks' && risks.map((r) => (
          <div key={r.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{r.risk_type.replace(/_/g, ' ')} · {r.severity}</h3>
            <p className={`mt-1 text-sm ${muted}`}>Probability {(r.probability * 100).toFixed(0)}%</p>
            <p className="mt-2 text-sm">{r.business_impact}</p>
            <p className="mt-1 text-sm">Mitigation: {r.mitigation_plan}</p>
          </div>
        ))}
        {tab === 'recommendations' && recommendations.map((r) => (
          <div key={r.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{r.title}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{r.type.replace(/_/g, ' ')} · Confidence {(r.confidence * 100).toFixed(0)}% · Savings {fmtMoney(r.expected_savings)}</p>
            <p className="mt-2 text-sm">{r.business_explanation}</p>
          </div>
        ))}
        {tab === 'scenarios' && scenarios.map((s) => (
          <div key={s.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{s.name}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{s.scenario_type.replace(/_/g, ' ')}</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              <span>Service {s.service_level.toFixed(0)}%</span>
              <span>Cost {fmtMoney(s.supply_cost)}</span>
              <span>Risk {s.risk_score.toFixed(0)}</span>
            </div>
            {s.description && <p className="mt-2 text-sm">{s.description}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
